import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Position, PositionResponse } from '../models/Position';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PositionService {
  private readonly baseUrl = environment.microservices?.staff || environment.apiUrl || '/api';

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) { }

  /**
   * Busca cargos por departamento
   */
  getPositionsByDepartment(departmentId: number): Observable<PositionResponse> {
    const url = `${this.baseUrl}/position/department/${departmentId}`;
    
    return this.http.get<any>(url)
      .pipe(
        map(response => this.mapPositionResponse(response)),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao buscar cargos do departamento');
          return throwError(() => err);
        })
      );
  }

  /**
   * Visualiza detalhes de um cargo
   */
  getPositionById(positionId: number): Observable<Position> {
    const url = `${this.baseUrl}/position/view/${positionId}`;

    return this.http.get<any>(url)
      .pipe(
        map(response => {
          // API pode retornar { success, position, timestamp } ou o DTO direto
          const dto = response?.position ?? response;
          return this.mapPositionFromDto(dto);
        }),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao buscar cargo');
          return throwError(() => err);
        })
      );
  }

  /**
   * Mapeia a resposta da API para o formato esperado
   */
  private mapPositionResponse(response: any): PositionResponse {
    return {
      success: response.success || false,
      departmentId: response.departmentId || 0,
      count: response.count || 0,
      positions: (response.positions || []).map((dto: any) => this.mapPositionFromDto(dto)),
      timestamp: response.timestamp || new Date().toISOString()
    };
  }

  /**
   * Cria um novo cargo
   */
  createPosition(positionData: {
    departmentId: number;
    name: string;
    level?: string | null;
    description?: string | null;
    defaultWorkloadHours?: number;
    isActive?: boolean;
  }): Observable<Position> {
    const url = `${this.baseUrl}/position/create`;
    
    // Payload em camelCase
    const payload = {
      departmentId: positionData.departmentId,
      name: positionData.name,
      level: positionData.level || null,
      description: positionData.description || null,
      defaultWorkloadHours: positionData.defaultWorkloadHours !== undefined ? positionData.defaultWorkloadHours : 8,
      isActive: positionData.isActive !== undefined ? positionData.isActive : true
    };
    
    return this.http.post<any>(url, payload)
      .pipe(
        map(response => this.mapPositionFromDto(response)),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao criar cargo');
          return throwError(() => err);
        })
      );
  }

  /**
   * Atualiza um cargo existente
   */
  updatePosition(positionId: number, positionData: {
    departmentId: number;
    name: string;
    level?: string | null;
    description?: string | null;
    defaultWorkloadHours?: number;
    isActive?: boolean;
  }): Observable<Position> {
    const url = `${this.baseUrl}/position/update/${positionId}`;

    const payload = {
      departmentId: positionData.departmentId,
      name: positionData.name,
      level: positionData.level || null,
      description: positionData.description || null,
      defaultWorkloadHours: positionData.defaultWorkloadHours !== undefined ? positionData.defaultWorkloadHours : 8,
      isActive: positionData.isActive !== undefined ? positionData.isActive : true
    };

    return this.http.put<any>(url, payload)
      .pipe(
        map(response => this.mapPositionFromDto(response)),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao atualizar cargo');
          return throwError(() => err);
        })
      );
  }

  /**
   * Exclui um cargo
   */
  deletePosition(positionId: number): Observable<void> {
    const url = `${this.baseUrl}/position/delete/${positionId}`;

    return this.http.delete<any>(url)
      .pipe(
        map(() => void 0),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao excluir cargo');
          return throwError(() => err);
        })
      );
  }

  /**
   * Ativa um cargo
   */
  activatePosition(positionId: number): Observable<Position> {
    const url = `${this.baseUrl}/position/${positionId}/activate`;

    return this.http.post<any>(url, {})
      .pipe(
        map(response => this.mapPositionFromDto(response)),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao ativar cargo');
          return throwError(() => err);
        })
      );
  }

  /**
   * Desativa um cargo
   */
  deactivatePosition(positionId: number): Observable<Position> {
    const url = `${this.baseUrl}/position/${positionId}/deactivate`;

    return this.http.post<any>(url, {})
      .pipe(
        map(response => this.mapPositionFromDto(response)),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao desativar cargo');
          return throwError(() => err);
        })
      );
  }

  /**
   * Mapeia DTO do backend para interface Position
   * O backend retorna campos em PascalCase (Name, Level, Description, etc.)
   */
  private mapPositionFromDto(dto: any): Position {
    return {
      id: dto.Id ?? dto.id ?? 0,
      departmentId: dto.DepartmentId ?? dto.departmentId ?? 0,
      name: dto.Name ?? dto.name ?? '',
      level: dto.Level ?? dto.level ?? null,
      description: dto.Description ?? dto.description ?? null,
      defaultWorkloadHours: dto.DefaultWorkloadHours ?? dto.defaultWorkloadHours ?? 8,
      isActive: dto.IsActive ?? dto.isActive ?? true,
      createdAt: dto.CreatedAt ?? dto.createdAt ?? '',
      updatedAt: dto.UpdatedAt ?? dto.updatedAt ?? null,
      createdBy: dto.CreatedBy ?? dto.createdBy ?? null,
      updatedBy: dto.UpdatedBy ?? dto.updatedBy ?? null
    };
  }
}

